import { useEffect, useRef, useState } from 'react';
import { Mic, Square, Send, Sparkles, Search, ChevronDown } from 'lucide-react';
import { api } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { SEEK } from '@/constants/testIds';
import { toast } from 'sonner';

const SUGGESTIONS = [
  'Scan every source for dryers under $30 within 35 miles of Akron',
  'Find free refrigerators near me',
  'Show couches under $50 within 25 miles',
  'Look for power tools posted today',
  'Free furniture within 40 miles',
];

const Seek = () => {
  const [text, setText] = useState('');
  const [interpreted, setInterpreted] = useState(null);
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const navigate = useNavigate();

  const interpret = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post('/ari/interpret', { text });
      setInterpreted(data.filters);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ari could not interpret that');
    } finally {
      setBusy(false);
    }
  };

  const startScan = async (sourceKeys = null) => {
    setBusy(true);
    try {
      const payload = interpreted
        ? { command_text: text, filters: interpreted, source_keys: sourceKeys }
        : { command_text: text, source_keys: sourceKeys };
      const { data } = await api.post('/scans/start', payload);
      toast.success('Scan started');
      navigate(`/results/${data.scan_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed to start');
    } finally {
      setBusy(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      mediaRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => chunksRef.current.push(e.data);
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach((t) => t.stop());
        const fd = new FormData();
        fd.append('file', blob, 'voice.webm');
        try {
          const { data } = await api.post('/ari/voice/transcribe', fd);
          if (data.text) setText(data.text);
        } catch {
          toast.error('Transcription failed');
        }
      };
      rec.start();
      setRecording(true);
    } catch {
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = () => {
    if (mediaRef.current?.state === 'recording') mediaRef.current.stop();
    setRecording(false);
  };

  return (
    <div className="space-y-8 zita-fade-up">
      <div className="text-center space-y-3">
        <div className="text-xs uppercase tracking-[0.18em] text-zita-mocha">Zita Command</div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight text-zita-espresso">
          What are you looking for?
        </h1>
        <p className="text-zita-mocha max-w-xl mx-auto">
          Tell Ari in your own words. She'll interpret, scan every source, and organize
          the best opportunities by platform.
        </p>
      </div>

      <div className="zita-card p-4 sm:p-6 max-w-3xl mx-auto">
        <div className="flex items-center gap-2">
          <Search size={18} className="text-zita-cocoa shrink-0" />
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && text.trim()) interpret(); }}
            placeholder="Scan every source for dryers under $30 within 35 miles of Akron…"
            data-testid={SEEK.commandBar}
            className="flex-1 bg-transparent outline-none text-base sm:text-lg placeholder:text-zita-muted"
          />
          <button
            type="button"
            onClick={recording ? stopRecording : startRecording}
            data-testid={SEEK.voiceButton}
            className={`p-3 rounded-full ${
              recording ? 'bg-red-100 text-red-600' : 'text-zita-mocha hover:bg-zita-sand'
            }`}
            aria-label="Voice"
          >
            {recording ? <Square size={18} /> : <Mic size={18} />}
          </button>
          <button
            type="button"
            onClick={interpret}
            disabled={busy || !text.trim()}
            data-testid={SEEK.confirmSearch}
            className="zita-btn-ari flex items-center gap-2"
          >
            <Sparkles size={14} /> Interpret
          </button>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => startScan(null)}
            disabled={busy || !text.trim()}
            data-testid={SEEK.scanAllButton}
            className="zita-btn-primary text-sm inline-flex items-center gap-2"
          >
            {busy ? 'Starting…' : 'Scan all sources'}
          </button>
          <span className="text-xs text-zita-muted">
            One-tap scan — Ari interprets and runs in the background.
          </span>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setText(s)}
              data-testid={SEEK.suggestionChip}
              className="text-xs px-3 py-1.5 rounded-full ari-chip hover:opacity-90"
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {interpreted && (
        <div data-testid={SEEK.interpretedBox} className="zita-card p-6 max-w-3xl mx-auto space-y-4 zita-fade-up">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="ari-accent" />
              <span className="text-xs uppercase tracking-wider text-zita-mocha">
                Ari's interpretation — confirm or adjust
              </span>
            </div>
            <button
              type="button"
              onClick={() => setShowFilters((v) => !v)}
              data-testid={SEEK.editInterpretation}
              className="text-sm text-zita-cocoa flex items-center gap-1"
            >
              {showFilters ? 'Hide' : 'Edit'} filters <ChevronDown size={14} className={showFilters ? 'rotate-180' : ''} />
            </button>
          </div>
          <Interpretation filters={interpreted} onChange={setInterpreted} showEdit={showFilters} />
          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-zita-taupe/60">
            <button
              type="button"
              onClick={() => startScan(null)}
              disabled={busy}
              data-testid={SEEK.scanAllButton}
              className="zita-btn-primary text-sm"
            >
              {busy ? 'Scanning…' : 'Scan all sources'}
            </button>
            <span className="text-xs text-zita-muted ml-auto">Scan-only. Zita never messages sellers.</span>
          </div>
        </div>
      )}
    </div>
  );
};

const Interpretation = ({ filters, onChange, showEdit }) => {
  const set = (k, v) => onChange({ ...filters, [k]: v });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <Info label="Item" value={filters.item_query || '—'} />
        <Info label={filters.free_only ? 'Free only' : 'Max price'} value={filters.free_only ? 'Yes' : (filters.max_price ? `$${filters.max_price}` : '—')} />
        <Info label="Location" value={filters.location_text || '—'} />
        <Info label="Radius" value={`${filters.radius_miles} mi`} />
      </div>
      {showEdit && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-xs text-zita-mocha space-y-1">
            <span>Item query</span>
            <input
              value={filters.item_query || ''}
              onChange={(e) => set('item_query', e.target.value)}
              className="zita-input w-full px-3 py-2 text-sm"
            />
          </label>
          <label className="text-xs text-zita-mocha space-y-1">
            <span>Max price</span>
            <input
              type="number"
              value={filters.max_price ?? ''}
              onChange={(e) => set('max_price', e.target.value ? Number(e.target.value) : null)}
              data-testid={SEEK.maxPriceInput}
              className="zita-input w-full px-3 py-2 text-sm"
            />
          </label>
          <label className="text-xs text-zita-mocha space-y-1">
            <span>Location</span>
            <input
              value={filters.location_text || ''}
              onChange={(e) => set('location_text', e.target.value)}
              data-testid={SEEK.locationInput}
              className="zita-input w-full px-3 py-2 text-sm"
            />
          </label>
          <label className="text-xs text-zita-mocha space-y-1">
            <span>Radius (mi)</span>
            <input
              type="number"
              value={filters.radius_miles ?? 35}
              onChange={(e) => set('radius_miles', Number(e.target.value))}
              data-testid={SEEK.radiusInput}
              className="zita-input w-full px-3 py-2 text-sm"
            />
          </label>
          <label className="text-xs text-zita-mocha flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!filters.free_only}
              onChange={(e) => set('free_only', e.target.checked)}
              data-testid={SEEK.freeOnlyToggle}
            />
            Free items only
          </label>
        </div>
      )}
    </div>
  );
};

const Info = ({ label, value }) => (
  <div className="zita-card-flat p-3">
    <div className="text-[10px] uppercase tracking-wider text-zita-muted">{label}</div>
    <div className="font-medium text-zita-espresso truncate">{value}</div>
  </div>
);

export default Seek;
